/**
 * PortalPermitPage — Public permit discovery portals (no login required)
 * /portal/permit-discovery | /portal/residential | /portal/events
 */
import React from 'react';
import {
  Box, Container, Typography, Button, Paper, Stack,
} from '@mui/material';
import { ArrowBack as BackIcon, Build as BuildIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PermitWizardDemo from './demo/PermitWizardDemo';
import ResidentialPermitDemo from './demo/ResidentialPermitDemo';
import EventsPermitDemo from './demo/EventsPermitDemo';
import PersonalAssistantDemo from './demo/PersonalAssistantDemo';
import BrandLogo from '../components/BrandLogo';
import InterviewFlowNavbar from '../components/InterviewFlow/InterviewFlowNavbar';

const TEAL = '#0d7377';
const NAVY = '#1e3a5f';

export default function PortalPermitPage() {
  const { portal } = useParams();
  const navigate = useNavigate();

  const handleBack = () => navigate(-1);
  const handleStudio = () => navigate('/studio');
  const handleSignup = () => navigate('/signup');
  const isPermitPortal = !portal || ['permit-discovery', 'residential', 'events'].includes(portal);

  const titles = {
    'permit-discovery': 'Permit Discovery',
    residential: 'Residential Permits',
    events: 'Special Events Permits',
    'personal-assistant': 'Personal Assistant',
  };
  const title = titles[portal] || 'Permit Discovery';

  const subtitles = {
    'personal-assistant': 'Emails, meetings, tasks — make your day easier.',
  };
  const subtitle = subtitles[portal] ?? "Tell us about your project and we'll suggest which permits you may need.";

  const DemoComponent = {
    'permit-discovery': PermitWizardDemo,
    residential: ResidentialPermitDemo,
    events: EventsPermitDemo,
    'personal-assistant': PersonalAssistantDemo,
  }[portal] || PermitWizardDemo;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      <InterviewFlowNavbar />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2,
          }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button size="small" startIcon={<BackIcon />} onClick={handleBack}>
                Back
              </Button>
              <BrandLogo size="small" orientation="horizontal" showTagline={false} iconSize={36} />
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {isPermitPortal && (
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="public-sector-outbound"
                  href="https://interviewflow.studio/publicSector/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public Sector (BaigLabs)
                </Button>
              )}
              <Button variant="outlined" size="small" onClick={handleStudio} startIcon={<BuildIcon />}>
                Customize in Studio
              </Button>
              <Button variant="contained" size="small" onClick={handleSignup} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0a5c5f' } }}>
                Get Started
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <DemoComponent onBack={handleBack} />
        </Paper>
      </Container>
    </Box>
  );
}
